import { useMemo, useState } from 'react';

import { useDebounce } from './use-debounce';
import { colorHexWithOpacity } from '../../const/colors';
import { getCategoryDataByName } from '../../utils/hardcodes';

export interface BaseFilterState {
  mediaTypes: string[];
  skillCategoryIds: string[];
  searchQuery: string;
}

export interface ResourceFacets {
  skillCategories?: Array<{
    id: string;
    title: string;
  }>;
  mediaTypes?: Array<{
    id: string;
    title: string;
  }>;
  locations?: Array<{
    id: string;
    title: string;
  }>;
}

export interface SkillCategoryOption {
  label: string;
  value: string;
  color: string;
}

export interface MediaTypeOption {
  label: string;
  value: string;
}

export interface LocationOption {
  label: string;
  value: string;
}

interface UseResourceFiltersOptions<T extends BaseFilterState> {
  initialFilters: T;
  debounceDelay?: number;
  onFilterChange?: (filters: T) => void;
}

export function useResourceFilters<T extends BaseFilterState>({
  initialFilters,
  debounceDelay = 300,
  onFilterChange,
}: UseResourceFiltersOptions<T>) {
  const [filters, setFilters] = useState<T>(initialFilters);
  const [currentPage, setCurrentPage] = useState(0);
  const [isReset, setIsReset] = useState(false);

  const debouncedSearchQuery = useDebounce(filters.searchQuery, debounceDelay);

  const debouncedFilters = useMemo(
    () => ({
      ...filters,
      searchQuery: debouncedSearchQuery,
    }),
    [filters, debouncedSearchQuery]
  );

  const updateFilters = (newFilters: Partial<T>) => {
    setCurrentPage(0);
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    onFilterChange?.(updatedFilters);
  };

  const resetFilters = () => {
    setCurrentPage(0);
    setIsReset(true);
    setFilters(initialFilters);
    onFilterChange?.(initialFilters);
  };

  const hasActiveFilters = useMemo(() => {

    return (
      filters.mediaTypes.length > 0 ||
      filters.skillCategoryIds.length > 0 ||
      filters.searchQuery.length > 0 ||
      ('hotTopics' in filters && (filters as any).hotTopics.length > 0) ||
      ('locations' in filters && (filters as any).locations.length > 0) ||
      ('status' in filters && (filters as any).status.length > 0) ||
      ('updatedFrom' in filters && (filters as any).updatedFrom)
    );
  }, [filters]);

  return {
    filters,
    debouncedFilters,
    currentPage,
    setCurrentPage,
    isReset,
    setIsReset,
    updateFilters,
    resetFilters,
    hasActiveFilters,
  };
}

export function useResourceFilterOptions(
  facets?: ResourceFacets,
  valueType: 'id' | 'title' = 'title'
) {
  const skillCategoryOptions = useMemo(() => {
    return facets?.skillCategories?.map(({ title, id }) => ({
      label: title,
      value: valueType === 'id' ? id : title,
      color: colorHexWithOpacity(getCategoryDataByName(title).color, 0.5),
    }));
  }, [facets?.skillCategories, valueType]);

  const uniqueSkillCategoryOptions = useMemo(() => {
    return Array.from(new Map(skillCategoryOptions?.map((sc) => [sc.label, sc])).values());
  }, [skillCategoryOptions]);

  const mediaTypeOptions = useMemo(() => {
    return facets?.mediaTypes?.map((mediaType) => ({
      label: mediaType.title,
      value: valueType === 'id' ? mediaType.id : mediaType.title,
    }));
  }, [facets?.mediaTypes, valueType]);

  const locationOptions = useMemo(() => {
    return facets?.locations?.map((location) => ({
      label: location.title,
      value: valueType === 'id' ? location.id : location.title,
    }));
  }, [facets?.locations, valueType]);

  return {
    skillCategoryOptions,
    uniqueSkillCategoryOptions,
    mediaTypeOptions,
    locationOptions,
  };
}
